import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Pause, Play } from "lucide-react";
import { useLang } from "@/i18n";
import { HOME_COPY as C } from "@/data/homeContent";
import { HOME_HERO_SEQUENCE, HOME_HERO_WIDE_SEQUENCE, HOME_MEDIA } from "@/data/homeMedia";
import { homeImageKey } from "@/data/homeArtDirection";
import images from "@/data/homeImageManifest.json";
import { HomeMedia } from "./HomeMedia";
import { useMediaActivity } from "./useMediaActivity";

export const HomeHeroScenes = () => {
  const { t } = useLang();
  const reduced = useReducedMotion();
  const frame = useRef(null);
  const { visible, foreground, saveData, mobile, breakpoint } = useMediaActivity(frame, true);
  const scenes = mobile ? HOME_HERO_SEQUENCE : HOME_HERO_WIDE_SEQUENCE;
  const [current, setIndex] = useState(0);
  const index = current % scenes.length;
  const [paused, setPaused] = useState(false);
  const [focused, setFocused] = useState(false);
  const stopped = paused || reduced || saveData || focused;
  useEffect(() => {
    if (!visible || !foreground || stopped) return;
    const timer = window.setTimeout(() => setIndex((value) => (value + 1) % scenes.length), 8000);
    return () => window.clearTimeout(timer);
  }, [index, visible, foreground, stopped, scenes.length]);
  useEffect(() => {
    if (!visible || !foreground || stopped) return;
    // Warm only the next image, never all films on first paint.
    const image = images[homeImageKey(HOME_MEDIA[scenes[(index + 1) % scenes.length]], breakpoint)];
    if (!image) return;
    const preload = new Image();
    preload.sizes = "100vw";
    preload.srcset = image.files.map((file) => `${file.src} ${file.width}w`).join(", ");
    preload.src = image.files[0].src;
  }, [index, visible, foreground, stopped, scenes, breakpoint]);
  const move = (offset) => { setPaused(true); setIndex((value) => (value + offset + scenes.length) % scenes.length); };
  return <>
    <div ref={frame} className="rh-hero-scenes" data-testid="home-hero-scenes" data-scene={scenes[index]}>
      <AnimatePresence initial={false}>
        <motion.div key={scenes[index]} className="rh-hero-scene" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : 1.2 }}>
          <HomeMedia name={scenes[index]} eager={index === 0} sizes="100vw" className="rh-hero-media" paused={paused || focused} showControls={false} />
        </motion.div>
      </AnimatePresence>
    </div>
    <div className="rh-hero-controls" data-testid="home-hero-controls" onFocus={() => setFocused(true)} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <button type="button" data-testid="hero-previous-scene" aria-label={t(C.previousScene)} title={t(C.previousScene)} onClick={() => move(-1)}><ArrowLeft size={16} /></button>
      {!reduced && !saveData && <button type="button" data-testid="hero-toggle-motion" aria-label={t(paused ? C.playScenes : C.pauseScenes)} title={t(paused ? C.playScenes : C.pauseScenes)} onClick={() => { setPaused((value) => !value); setFocused(false); }}>{paused ? <Play size={15} /> : <Pause size={15} />}</button>}
      <button type="button" data-testid="hero-next-scene" aria-label={t(C.nextScene)} title={t(C.nextScene)} onClick={() => move(1)}><ArrowRight size={16} /></button>
    </div>
  </>;
};